/**
 * Centralized fetch wrapper for admin pages that need to call backend
 * endpoints outside the standard react-admin dataProvider CRUD shape
 * (e.g. settings, loyalty actions, refunds, AI generation, stock alerts).
 *
 * Single source of truth for:
 *  - Bearer-token attachment (read from localStorage same as authProvider)
 *  - Content-Type defaulting
 *  - 401 → redirect to /login (matches authProvider behavior)
 *  - Uniform error shape so call sites can reliably notify(err.message)
 */

const BASE_URL = `${process.env.REACT_APP_API_URL ?? ''}/api`;
const TOKEN_KEY = 'authToken';

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

interface ApiOptions extends RequestInit {
  /**
   * JSON body — gets stringified, no need to set Content-Type. Takes
   * precedence over `body` when both are provided. Use `body` directly
   * for FormData / Blob / multipart uploads.
   */
  json?: unknown;
  /** Parse the response as JSON (default true). Set false for 204 / non-JSON. */
  parseJson?: boolean;
}

function readErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const b = body as Record<string, unknown>;
  if (Array.isArray(b.message)) return b.message.join(', ');
  if (typeof b.message === 'string') return b.message;
  if (typeof b.error === 'string') return b.error;
  return fallback;
}

/**
 * Fetch a JSON endpoint and return parsed body. Throws `ApiError` on non-2xx.
 * On 401, the caller should let the error bubble — react-admin's
 * `authProvider.checkError` is what triggers redirect; here we mirror by
 * dispatching a one-shot navigation as a backstop for code paths that don't
 * route through the dataProvider.
 */
export async function apiFetch<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const { json, parseJson = true, headers, ...rest } = opts;
  const token = localStorage.getItem(TOKEN_KEY);

  const finalHeaders: HeadersInit = {
    Accept: 'application/json',
    ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers ?? {}),
  };

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (res.status === 401) {
    // Backstop for callers that bypass dataProvider — clear token and let
    // the next navigation tick land on /login. We don't navigate here
    // imperatively to avoid coupling apiFetch to react-router.
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  }

  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty / non-JSON */ }
    throw new ApiError(res.status, body, readErrorMessage(body, `HTTP ${res.status}`));
  }

  if (!parseJson || res.status === 204) return undefined as T;
  // Some endpoints reply 200 with empty body — guard against JSON parse error.
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export const apiGet = <T = unknown>(path: string, opts?: ApiOptions) =>
  apiFetch<T>(path, { ...opts, method: 'GET' });

export const apiPost = <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
  apiFetch<T>(path, { ...opts, method: 'POST', json });

export const apiPatch = <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
  apiFetch<T>(path, { ...opts, method: 'PATCH', json });

export const apiPut = <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
  apiFetch<T>(path, { ...opts, method: 'PUT', json });

export const apiDelete = <T = unknown>(path: string, opts?: ApiOptions) =>
  apiFetch<T>(path, { ...opts, method: 'DELETE' });
